import { useQuery } from '@tanstack/react-query';
import { formatUnits } from 'viem';
import { ERC20_ABI } from '../config/oft';
import { DEPOSIT_CHAIN_BY_ID } from '../config/chains';
import { getSourceChainPublicClient } from '../utils/getSourceChainPublicClient';

export interface SourceTokenBalanceData {
  /** Raw balance as BigInt */
  raw: bigint;
  /** Human-readable formatted string (e.g. "100.00") */
  formatted: string;
  /** Decimals of the token (6 for USDT0/USDT) */
  decimals: number;
}

/**
 * Queries the token balance on a specific source chain for a given address.
 *
 * Uses a chain-specific publicClient (NOT the wagmi-connected wallet chain).
 * Token address defaults to the chain config's primary token, but can be
 * overridden via `tokenAddress` to query a different token (e.g. USDC).
 *
 * @param chainId - EVM chain ID to query on
 * @param address - Wallet address to query balance for
 * @param tokenAddress - Optional override for token address (e.g. USDC on Ink)
 */
export function useSourceTokenBalance(
  chainId: number | undefined,
  address: `0x${string}` | undefined,
  tokenAddress?: `0x${string}`,
) {
  return useQuery<SourceTokenBalanceData>({
    queryKey: ['sourceTokenBalance', chainId, address, tokenAddress],
    queryFn: async () => {
      if (!chainId || !address) {
        throw new Error('chainId and address are required');
      }

      const chainConfig = DEPOSIT_CHAIN_BY_ID[chainId];
      if (!chainConfig) {
        throw new Error(`No chain config for chainId ${chainId}`);
      }

      const publicClient = getSourceChainPublicClient(chainId);
      // Use the provided tokenAddress override, or fall back to chain default
      const resolvedTokenAddress = tokenAddress ?? chainConfig.tokenAddress;

      const raw = await publicClient.readContract({
        address: resolvedTokenAddress,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [address],
      });

      return {
        raw,
        formatted: formatUnits(raw, chainConfig.tokenDecimals),
        decimals: chainConfig.tokenDecimals,
      };
    },
    enabled: !!chainId && !!address,
    staleTime: 15_000,
  });
}
